import { camelizeKeys } from 'humps';
import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  CHANGE_IMPACT_ROLES,
  EMPLOYEE_PROFILE_PER_PAGE,
} from '../constants/employeeProfile.js';
import {
  EMPLOYEE_TABS_ENDPOINTS,
  EMPLOYEE_TABS_RESPONSE_KEYS,
} from '../lib/contracts/employeeProfileTabs.js';
import {
  employee as employeeSchema,
  employeeNormalize as employeeNormalizeSchema,
  employeeSearch as employeeSearchSchema,
  employeeSearchNormalize as employeeSearchNormalizeSchema,
  employeeSimplified as employeeSimplifiedSchema,
  employeeSimplifiedNormalize as employeeSimplifiedNormalizeSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectCurrentOrganizationId } from './auth.js';
import { selectEntities } from './entities.js';

// API Actions
const RETRIEVE_EMPLOYEES_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEES_REQUEST';
const RETRIEVE_EMPLOYEES_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEES_SUCCESS';
const RETRIEVE_EMPLOYEES_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEES_FAILURE';

const RETRIEVE_SIMPLIFIED_EMPLOYEES_REQUEST =
  'changesync/employees/RETRIEVE_SIMPLIFIED_EMPLOYEES_REQUEST';
const RETRIEVE_SIMPLIFIED_EMPLOYEES_SUCCESS =
  'changesync/employees/RETRIEVE_SIMPLIFIED_EMPLOYEES_SUCCESS';
const RETRIEVE_SIMPLIFIED_EMPLOYEES_FAILURE =
  'changesync/employees/RETRIEVE_SIMPLIFIED_EMPLOYEES_FAILURE';

const RETRIEVE_SIMPLIFIED_QUIET_REQUEST =
  'changesync/employees/RETRIEVE_SIMPLIFIED_QUIET_REQUEST';
const RETRIEVE_SIMPLIFIED_QUIET_SUCCESS =
  'changesync/employees/RETRIEVE_SIMPLIFIED_QUIET_SUCCESS';
const RETRIEVE_SIMPLIFIED_QUIET_FAILURE =
  'changesync/employees/RETRIEVE_SIMPLIFIED_QUIET_FAILURE';

const CLEAR_SIMPLIFIED_EMPLOYEES_RESULT =
  'changesync/employees/CLEAR_SIMPLIFIED_EMPLOYEES_RESULT';

const SEARCH_EMPLOYEES_REQUEST =
  'changesync/employees/SEARCH_EMPLOYEES_REQUEST';
const SEARCH_EMPLOYEES_SUCCESS =
  'changesync/employees/SEARCH_EMPLOYEES_SUCCESS';
const SEARCH_EMPLOYEES_FAILURE =
  'changesync/employees/SEARCH_EMPLOYEES_FAILURE';
const RETRIEVE_EMPLOYEE_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEE_REQUEST';
const RETRIEVE_EMPLOYEE_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEE_SUCCESS';
const RETRIEVE_EMPLOYEE_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEE_FAILURE';
const RETRIEVE_EMPLOYEE_STATUS_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEE_STATUS_REQUEST';
const RETRIEVE_EMPLOYEE_STATUS_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEE_STATUS_SUCCESS';
const RETRIEVE_EMPLOYEE_STATUS_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEE_STATUS_FAILURE';
const CREATE_EMPLOYEE_REQUEST = 'changesync/employees/CREATE_EMPLOYEE_REQUEST';
const CREATE_EMPLOYEE_SUCCESS = 'changesync/employees/CREATE_EMPLOYEE_SUCCESS';
const CREATE_EMPLOYEE_FAILURE = 'changesync/employees/CREATE_EMPLOYEE_FAILURE';
const UPDATE_EMPLOYEE_REQUEST = 'changesync/employees/UPDATE_EMPLOYEE_REQUEST';
const UPDATE_EMPLOYEE_SUCCESS = 'changesync/employees/UPDATE_EMPLOYEE_SUCCESS';
const UPDATE_EMPLOYEE_FAILURE = 'changesync/employees/UPDATE_EMPLOYEE_FAILURE';
const DELETE_EMPLOYEE_REQUEST = 'changesync/employees/DELETE_EMPLOYEE_REQUEST';
const DELETE_EMPLOYEE_SUCCESS = 'changesync/employees/DELETE_EMPLOYEE_SUCCESS';
const DELETE_EMPLOYEE_FAILURE = 'changesync/employees/DELETE_EMPLOYEE_FAILURE';

// Employee profile tabs
const RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_REQUEST';
const RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_SUCCESS';
const RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_FAILURE';
const RETRIEVE_EMPLOYEE_TASKS_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEE_TASKS_REQUEST';
const RETRIEVE_EMPLOYEE_TASKS_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEE_TASKS_SUCCESS';
const RETRIEVE_EMPLOYEE_TASKS_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEE_TASKS_FAILURE';
const RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_REQUEST =
  'changesync/employees/RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_REQUEST';
const RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_SUCCESS =
  'changesync/employees/RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_SUCCESS';
const RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_FAILURE =
  'changesync/employees/RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_FAILURE';

// Temporary: true serves the employee profile tabs from src/mocks/employeeProfileTabs
const EMPLOYEE_TABS_MOCK = process.env.EMPLOYEE_TABS_MOCK === 'true';

// Search Actions
const SEARCH_EMPLOYEES = 'changesync/employees/SEARCH_EMPLOYEES';

// Initial State
const initialEmployeeListState = {
  items: [],
  total: 0,
  page: 1,
  perPage: EMPLOYEE_PROFILE_PER_PAGE,
  totalPages: 0,
  employeeId: null,
  requestId: null,
  pending: false,
  success: false,
  error: null,
};

const initialState = {
  retrieveEmployees: {
    employeeIds: [],
    pending: false,
    success: false,
    error: null,
  },

  retrieveSimplifiedEmployees: {
    employeeIds: [],
    pending: false,
    success: false,
    error: null,
  },

  searchEmployees: {
    employeeIds: [],
    pending: false,
    success: false,
    error: null,

    searchParams: {
      total: 0,
      page: 1,
      perPage: 25,
      totalPages: 0,
      totalObjectsPerOrganization: 0,
      search: '',
    },
  },

  retrieveEmployee: {
    pending: false,
    success: false,
    error: null,
  },

  employeeChangeImpacts: {
    [CHANGE_IMPACT_ROLES.OWNER]: initialEmployeeListState,
    [CHANGE_IMPACT_ROLES.IMPACTED]: initialEmployeeListState,
  },

  employeeTasks: initialEmployeeListState,

  employeeStakeholderProfiles: {
    projects: [],
    total: 0,
    employeeId: null,
    requestId: null,
    pending: false,
    success: false,
    error: null,
  },
};

// Employee profile tabs list helpers. requestId guards against a slower,
// older response (e.g. a previous search) overwriting a newer one.
function employeeListRequest(listState, { employeeId, requestId }) {
  return {
    ...listState,
    ...(listState.employeeId !== employeeId ? { items: [], total: 0 } : {}),
    employeeId,
    requestId,
    pending: true,
    success: false,
    error: null,
  };
}

function employeeListSuccess(listState, { payload, meta }, itemsKey) {
  if (meta.requestId !== listState.requestId) return listState;
  return {
    ...listState,
    items: payload[itemsKey] || [],
    total: payload.total || 0,
    page: payload.page || 1,
    perPage: payload.perPage || listState.perPage,
    totalPages: payload.totalPages || 0,
    pending: false,
    success: true,
  };
}

function employeeListFailure(listState, { error, meta }) {
  if (meta.requestId !== listState.requestId) return listState;
  return { ...listState, pending: false, error };
}

// Stakeholder profiles are requested without key camelization (dynamic field keys),
// so only `project` and `stakeholder_fields` are camelized here.
const normalizeStakeholderProjectGroups = (projects = []) =>
  projects.map((group) => ({
    project: camelizeKeys(group.project),
    stakeholderFields: camelizeKeys(group.stakeholder_fields || []),
    stakeholders: group.stakeholders || [],
  }));

const updateChangeImpactsRole = (state, role, updater) => ({
  ...state,
  employeeChangeImpacts: {
    ...state.employeeChangeImpacts,
    [role]: updater(state.employeeChangeImpacts[role]),
  },
});

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_EMPLOYEES_REQUEST:
      return {
        ...state,
        retrieveEmployees: {
          ...state.retrieveEmployees,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_EMPLOYEES_SUCCESS:
      return {
        ...state,
        retrieveEmployees: {
          ...state.retrieveEmployees,
          employeeIds: action.payload.result.employees,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_EMPLOYEES_FAILURE:
      return {
        ...state,
        retrieveEmployees: {
          ...state.retrieveEmployees,
          pending: false,
          error: action.error,
        },
      };

    case SEARCH_EMPLOYEES_REQUEST:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          pending: true,
          success: false,
          error: null,
        },
      };
    case SEARCH_EMPLOYEES_SUCCESS:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          employeeIds: action.payload.result.employees,
          pending: false,
          success: true,
          searchParams: {
            ...state.searchEmployees.searchParams,
            total: action.payload.result.total,
            page: action.payload.result.page,
            perPage: action.payload.result.perPage,
            totalPages: action.payload.result.totalPages,
            totalObjectsPerOrganization:
              action.payload.result.totalObjectsPerOrganization,
            search: action.meta.search,
          },
        },
      };
    case SEARCH_EMPLOYEES_FAILURE:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_EMPLOYEE_REQUEST:
      return {
        ...state,
        retrieveEmployee: {
          ...state.retrieveEmployee,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        retrieveEmployee: {
          ...state.retrieveEmployee,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_EMPLOYEE_FAILURE:
      return {
        ...state,
        retrieveEmployee: {
          ...state.retrieveEmployee,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_SIMPLIFIED_EMPLOYEES_REQUEST:
      return {
        ...state,
        retrieveSimplifiedEmployees: {
          ...state.retrieveSimplifiedEmployees,
          employeeIds: [],
          pending: !action.meta.isAutocomplete,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_SIMPLIFIED_EMPLOYEES_SUCCESS:
      return {
        ...state,
        retrieveSimplifiedEmployees: {
          ...state.retrieveSimplifiedEmployees,
          employeeIds: action.payload.result.employees,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_SIMPLIFIED_EMPLOYEES_FAILURE:
      return {
        ...state,
        retrieveSimplifiedEmployees: {
          ...state.retrieveSimplifiedEmployees,
          pending: false,
          error: action.error,
        },
      };

    case CLEAR_SIMPLIFIED_EMPLOYEES_RESULT:
      return {
        ...state,
        retrieveSimplifiedEmployees: {
          ...state.retrieveSimplifiedEmployees,
          employeeIds: [],
          pending: false,
          success: true,
        },
      };

    case CREATE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          pending: false,
          success: false,
          error: null,
          query: '',
        },
      };

    case UPDATE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          pending: false,
          success: false,
          error: null,
          query: '',
        },
      };

    case DELETE_EMPLOYEE_SUCCESS:
      return {
        ...state,
        searchEmployees: {
          ...state.searchEmployees,
          pending: false,
          success: false,
          error: null,
          query: '',
        },
      };

    case RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_REQUEST:
      return updateChangeImpactsRole(state, action.meta.role, (listState) =>
        employeeListRequest(listState, action.meta)
      );
    case RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_SUCCESS:
      return updateChangeImpactsRole(state, action.meta.role, (listState) =>
        employeeListSuccess(
          listState,
          action,
          EMPLOYEE_TABS_RESPONSE_KEYS.changeImpacts
        )
      );
    case RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_FAILURE:
      return updateChangeImpactsRole(state, action.meta.role, (listState) =>
        employeeListFailure(listState, action)
      );

    case RETRIEVE_EMPLOYEE_TASKS_REQUEST:
      return {
        ...state,
        employeeTasks: employeeListRequest(state.employeeTasks, action.meta),
      };
    case RETRIEVE_EMPLOYEE_TASKS_SUCCESS:
      return {
        ...state,
        employeeTasks: employeeListSuccess(
          state.employeeTasks,
          action,
          EMPLOYEE_TABS_RESPONSE_KEYS.tasks
        ),
      };
    case RETRIEVE_EMPLOYEE_TASKS_FAILURE:
      return {
        ...state,
        employeeTasks: employeeListFailure(state.employeeTasks, action),
      };

    case RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_REQUEST: {
      const listState = state.employeeStakeholderProfiles;
      return {
        ...state,
        employeeStakeholderProfiles: {
          ...listState,
          ...(listState.employeeId !== action.meta.employeeId
            ? { projects: [], total: 0 }
            : {}),
          employeeId: action.meta.employeeId,
          requestId: action.meta.requestId,
          pending: true,
          success: false,
          error: null,
        },
      };
    }
    case RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_SUCCESS: {
      const listState = state.employeeStakeholderProfiles;
      if (action.meta.requestId !== listState.requestId) return state;
      return {
        ...state,
        employeeStakeholderProfiles: {
          ...listState,
          projects: normalizeStakeholderProjectGroups(
            action.payload[EMPLOYEE_TABS_RESPONSE_KEYS.stakeholderProjects]
          ),
          total: action.payload.total || 0,
          pending: false,
          success: true,
        },
      };
    }
    case RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_FAILURE: {
      const listState = state.employeeStakeholderProfiles;
      if (action.meta.requestId !== listState.requestId) return state;
      return {
        ...state,
        employeeStakeholderProfiles: {
          ...listState,
          pending: false,
          error: action.error,
        },
      };
    }

    default:
      return state;
  }
};
// Action Creators

export const retrieveEmployees = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_EMPLOYEES_REQUEST,
        RETRIEVE_EMPLOYEES_SUCCESS,
        RETRIEVE_EMPLOYEES_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/employees`,
      method: HTTP_METHODS.GET,
      schema: employeeNormalizeSchema,
      query: { ...query },
    },
  });
};

export const searchEmployees = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_EMPLOYEES_REQUEST,
        SEARCH_EMPLOYEES_SUCCESS,
        SEARCH_EMPLOYEES_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/employees`,
      method: HTTP_METHODS.GET,
      schema: employeeSearchNormalizeSchema,
      query: { active: true, ...query },
    },

    meta: {
      search: query.search,
      debounce: {
        time: 400,
        key: SEARCH_EMPLOYEES,
      },
    },
  });
};

export const retrieveSimplifiedEmployees =
  ({
    search,
    groupIds,
    page,
    perPage,
    objectType,
    objectId,
    excludingGroupIds,
    employeeIds,
    active = true,
    autocomplete = true,
  }) =>
  (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());

    // Quiet action with no store changes to get temporary employee entities directly in components
    // only for autocomplete = true
    const types = autocomplete
      ? [
          RETRIEVE_SIMPLIFIED_QUIET_REQUEST,
          RETRIEVE_SIMPLIFIED_QUIET_SUCCESS,
          RETRIEVE_SIMPLIFIED_QUIET_FAILURE,
        ]
      : [
          RETRIEVE_SIMPLIFIED_EMPLOYEES_REQUEST,
          RETRIEVE_SIMPLIFIED_EMPLOYEES_SUCCESS,
          RETRIEVE_SIMPLIFIED_EMPLOYEES_FAILURE,
        ];

    if (search && search.length < 2 && autocomplete) {
      return dispatch({
        type: CLEAR_SIMPLIFIED_EMPLOYEES_RESULT,
      });
    }
    return dispatch({
      [CALL_API]: {
        types,
        endpoint: `organizations/${organizationId}/employees/autocomplete`,
        method: HTTP_METHODS.GET,
        schema: !autocomplete ? employeeSimplifiedNormalizeSchema : null,
        query: {
          page,
          perPage,
          active,
          autocomplete,
          search,
          groupIds,
          employeeIds,
          excludingGroupIds,
          objectType,
          objectId,
        },
      },
      meta: {
        search,
        isAutocomplete: autocomplete,
        debounce: {
          time: autocomplete ? 400 : 0,
          key: SEARCH_EMPLOYEES,
        },
      },
    });
  };

export const retrieveEmployee = (id) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_EMPLOYEE_REQUEST,
      RETRIEVE_EMPLOYEE_SUCCESS,
      RETRIEVE_EMPLOYEE_FAILURE,
    ],
    endpoint: `employees/${id}`,
    method: HTTP_METHODS.GET,
    schema: employeeSchema,
  },
});

export const retrieveEmployeeStatus = (id) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_EMPLOYEE_STATUS_REQUEST,
      RETRIEVE_EMPLOYEE_STATUS_SUCCESS,
      RETRIEVE_EMPLOYEE_STATUS_FAILURE,
    ],
    endpoint: `employees/${id}/show_alert`,
    method: HTTP_METHODS.GET,
  },
});

export const createEmployee = (organizationId, employee) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        CREATE_EMPLOYEE_REQUEST,
        CREATE_EMPLOYEE_SUCCESS,
        CREATE_EMPLOYEE_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/employees`,
      method: HTTP_METHODS.POST,
      schema: employeeSchema,
      body: { employee },
    },
  });

export const updateEmployee = (employee) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        UPDATE_EMPLOYEE_REQUEST,
        UPDATE_EMPLOYEE_SUCCESS,
        UPDATE_EMPLOYEE_FAILURE,
      ],
      endpoint: `employees/${employee.id}`,
      method: HTTP_METHODS.PUT,
      schema: employeeSchema,
      body: { employee },
    },
  });

// Search input debouncing happens on the URL (?search=) level, see useDebouncedUrlSearch
export const searchEmployeeInitiatives = (id, query) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_EMPLOYEE_REQUEST,
        RETRIEVE_EMPLOYEE_SUCCESS,
        RETRIEVE_EMPLOYEE_FAILURE,
      ],
      endpoint: `employees/${id}`,
      method: HTTP_METHODS.GET,
      schema: employeeSchema,
      query: { search: query || undefined },
    },
  });

// Employee profile tabs. All three endpoints go through employeeTabsApiAction,
// which switches between the real API (CALL_API) and the mock transport.
let employeeTabsRequestSeq = 0;

const employeeTabsApiAction = ({
  types,
  endpoint,
  query,
  disabledCamelizeKeys,
  meta,
}) => {
  if (EMPLOYEE_TABS_MOCK) {
    return (dispatch) =>
      import('../mocks/employeeProfileTabs/index.js').then(({ mockCallApi }) =>
        dispatch(
          mockCallApi({ types, endpoint, query, disabledCamelizeKeys, meta })
        )
      );
  }

  return {
    [CALL_API]: {
      types,
      endpoint,
      method: HTTP_METHODS.GET,
      query,
      disabledCamelizeKeys,
    },
    meta,
  };
};

export const retrieveEmployeeChangeImpacts = (
  employeeId,
  {
    role,
    search,
    sortBy,
    sortType,
    page = 1,
    perPage = EMPLOYEE_PROFILE_PER_PAGE,
  }
) =>
  employeeTabsApiAction({
    types: [
      RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_REQUEST,
      RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_SUCCESS,
      RETRIEVE_EMPLOYEE_CHANGE_IMPACTS_FAILURE,
    ],
    endpoint: EMPLOYEE_TABS_ENDPOINTS.changeImpacts(employeeId),
    query: {
      role,
      search: search || undefined,
      sortBy,
      sortType,
      page,
      perPage,
    },
    meta: { employeeId, role, requestId: (employeeTabsRequestSeq += 1) },
  });

export const retrieveEmployeeTasks = (
  employeeId,
  { search, page = 1, perPage = EMPLOYEE_PROFILE_PER_PAGE }
) =>
  employeeTabsApiAction({
    types: [
      RETRIEVE_EMPLOYEE_TASKS_REQUEST,
      RETRIEVE_EMPLOYEE_TASKS_SUCCESS,
      RETRIEVE_EMPLOYEE_TASKS_FAILURE,
    ],
    endpoint: EMPLOYEE_TABS_ENDPOINTS.tasks(employeeId),
    query: { search: search || undefined, page, perPage },
    meta: { employeeId, requestId: (employeeTabsRequestSeq += 1) },
  });

export const retrieveEmployeeStakeholderProfiles = (employeeId, { search }) =>
  employeeTabsApiAction({
    types: [
      RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_REQUEST,
      RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_SUCCESS,
      RETRIEVE_EMPLOYEE_STAKEHOLDER_PROFILES_FAILURE,
    ],
    endpoint: EMPLOYEE_TABS_ENDPOINTS.stakeholders(employeeId),
    query: { search: search || undefined },
    // stakeholder rows have dynamic keys named after project custom fields
    disabledCamelizeKeys: true,
    meta: { employeeId, requestId: (employeeTabsRequestSeq += 1) },
  });

export const deleteEmployee = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_EMPLOYEE_REQUEST,
      DELETE_EMPLOYEE_SUCCESS,
      DELETE_EMPLOYEE_FAILURE,
    ],
    endpoint: `employees/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

// Selectors

export const selectRetrieveEmployeesState = (state) =>
  state.employees.retrieveEmployees;
export const selectSearchEmployeesState = (state) =>
  state.employees.searchEmployees;
export const selectRetrieveSimplifiedEmployeesState = (state) =>
  state.employees.retrieveSimplifiedEmployees;

const selectEmployeeIds = (state) =>
  state.employees.retrieveEmployees.employeeIds;
const selectSearchEmployeeIds = (state) =>
  state.employees.searchEmployees.employeeIds;
const selectSimplifiedEmployeeIds = (state) =>
  state.employees.retrieveSimplifiedEmployees.employeeIds;

export const selectEmployees = createSelector(
  selectEmployeeIds,
  selectEntities,
  (employeeIds, entities) =>
    denormalize(employeeIds, [employeeSchema], entities)
);

export const selectSearchResult = createSelector(
  selectSearchEmployeeIds,
  selectEntities,
  (employeeIds, entities) =>
    denormalize(employeeIds, [employeeSearchSchema], entities)
);

export const selectSimplifiedEmployees = createSelector(
  selectSimplifiedEmployeeIds,
  selectEntities,
  (employeeIds, entities) =>
    denormalize(employeeIds, [employeeSimplifiedSchema], entities)
);

export const selectSimplifiedEmployee = (id) => (state) => {
  return denormalize(id, employeeSimplifiedSchema, state.entities);
};

export const selectFlattenedEmployees = createSelector(
  selectEmployees,
  (employees) => {
    const flattenedEmployees = [];
    const pushEmployeeAndChildren = (employee) => {
      flattenedEmployees.push(employee);
      employee.children.forEach(pushEmployeeAndChildren);
    };
    employees.forEach(pushEmployeeAndChildren);
    return flattenedEmployees;
  }
);

export const selectEmployee = (id) => (state) => {
  return denormalize(id, employeeSchema, state.entities);
};

export const selectEmployeeChangeImpactsState = (role) => (state) =>
  state.employees.employeeChangeImpacts[role];

export const selectEmployeeTasksState = (state) =>
  state.employees.employeeTasks;

export const selectEmployeeStakeholderProfilesState = (state) =>
  state.employees.employeeStakeholderProfiles;
